import { motion, type Transition } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";

import { defaultAnimationTuningSettings, type GardenImageLayoutSettings } from "./animation-tuning";
import { landingPageAssets } from "./assets";
import { defaultAudioTuningSettings } from "./audio-tuning";
import { defaultPacketLightingSettings } from "./packet-lighting";
import { defaultPacketPoseSettings } from "./packet-pose";
import { RewardStaggerRow } from "./RewardStaggerRow";
import { SeedPacketMysteryPack } from "./SeedPacketMysteryPack";
import { getLayoutCssVariables, type LayoutScene } from "./layout-presets";
import { useLayoutSettings } from "./use-layout-settings";
import { useSeedPacketAudio } from "./use-seed-packet-audio";

type LandingPhase = "packet" | "revealing" | "revealed";
type RevealedScene = "packs" | "garden";
type MobileStore = "app" | "play";
type LandingAnimationStyle = CSSProperties & Record<`--${string}`, string>;

const tapCountToOpen = 4;
const revealedSceneCycleMs = 3000;
const animationSettings = defaultAnimationTuningSettings;
const audioSettings = defaultAudioTuningSettings;

function detectMobileStore(): MobileStore | undefined {
  if (typeof navigator === "undefined") {
    return undefined;
  }

  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: {
      platform?: string;
    };
  };
  const platform = navigatorWithUserAgentData.userAgentData?.platform ?? navigator.platform;
  const isAppleMobile =
    /iPad|iPhone|iPod/i.test(navigator.userAgent) ||
    (/Mac/i.test(platform) && navigator.maxTouchPoints > 1);

  if (isAppleMobile) {
    return "app";
  }

  if (/Android/i.test(`${navigator.userAgent} ${platform}`)) {
    return "play";
  }

  return undefined;
}

function TikTokIcon() {
  return (
    <svg aria-hidden="true" className="social-icon" viewBox="0 0 48 48">
      <path
        d="M28 7v23.1c0 7-4.4 10.9-10.5 10.9C12.1 41 8 37.2 8 32.3s4-8.7 9.2-8.7c1 0 2 .1 2.8.5v6.2a5 5 0 0 0-2.6-.7c-2 0-3.4 1.1-3.4 2.9 0 1.7 1.4 2.9 3.3 2.9 2 0 3.5-1.1 3.5-4.1V7H28zm.3 0c1 5.5 4.4 8.7 9.7 9.1v6.2c-3.8-.1-7.1-1.4-9.7-3.7V7z"
        fill="currentColor"
      />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg aria-hidden="true" className="social-icon" viewBox="0 0 48 48">
      <rect
        fill="none"
        height="32"
        rx="9"
        stroke="currentColor"
        strokeWidth="4.5"
        width="32"
        x="8"
        y="8"
      />
      <circle cx="24" cy="24" fill="none" r="7.2" stroke="currentColor" strokeWidth="4.5" />
      <circle cx="33.8" cy="14.4" fill="currentColor" r="2.5" />
    </svg>
  );
}

function DiscordIcon() {
  return (
    <svg aria-hidden="true" className="social-icon" viewBox="0 0 48 48">
      <path
        d="M36.9 13.9A31 31 0 0 0 29.4 12l-.7 1.5a25.7 25.7 0 0 0-9.4 0l-.7-1.5a31 31 0 0 0-7.5 1.9C6.4 20.8 5.2 27.5 5.8 34.1a30.4 30.4 0 0 0 9.2 4.6l1.9-3.1a19.8 19.8 0 0 1-3-1.5l.7-.6a22.3 22.3 0 0 0 18.8 0l.7.6c-1 .6-2 1.1-3 1.5l1.9 3.1a30.4 30.4 0 0 0 9.2-4.6c.7-7.6-1.2-14.2-5.3-20.2zM17.9 30c-1.8 0-3.2-1.7-3.2-3.8s1.4-3.8 3.2-3.8 3.3 1.7 3.2 3.8c0 2.1-1.4 3.8-3.2 3.8zm12.2 0c-1.8 0-3.2-1.7-3.2-3.8s1.4-3.8 3.2-3.8 3.3 1.7 3.2 3.8c0 2.1-1.4 3.8-3.2 3.8z"
        fill="currentColor"
      />
    </svg>
  );
}

/** Tagline sits at this % of the frame; garden fills the band above it. */
const gardenTaglineTopPercent = 73.632_812_5;
const gardenBandBottomGapPercent = 2.5;
const gardenMaxWidthCqw = 90;

function getGardenImageStyle(settings: GardenImageLayoutSettings): LandingAnimationStyle {
  const availableHeightPercent = Math.max(
    gardenTaglineTopPercent - settings.topPercent - gardenBandBottomGapPercent,
    18,
  );
  const fillScale = Math.max(settings.sizePercent, 1) / 100;
  const sizeCqh = availableHeightPercent * fillScale;
  // Garden art is 1:1; size by the smaller of width room and the vertical band.
  const sizeWidth = `min(${gardenMaxWidthCqw}cqw, ${sizeCqh}cqh)`;

  return {
    "--garden-image-left": "50%",
    "--garden-image-max-height": `${sizeCqh}cqh`,
    "--garden-image-top": `${settings.topPercent}%`,
    "--garden-image-width": sizeWidth,
  };
}

type LandingPageProps = {
  editorScene?: LayoutScene;
  isLayoutPreview?: boolean;
  mobileStoreOverride?: MobileStore;
};

export function LandingPage({
  editorScene,
  isLayoutPreview = false,
  mobileStoreOverride,
}: LandingPageProps) {
  const [detectedMobileStore] = useState<MobileStore | undefined>(detectMobileStore);
  const mobileStore = mobileStoreOverride ?? detectedMobileStore;
  const [phase, setPhase] = useState<LandingPhase>(
    !editorScene || editorScene === "packet" ? "packet" : "revealed",
  );
  const [openRun, setOpenRun] = useState(0);
  const [burstStartedAt, setBurstStartedAt] = useState(0);
  const [revealedScene, setRevealedScene] = useState<RevealedScene>(
    editorScene === "garden" ? "garden" : "packs",
  );
  const [tapProgress, setTapProgress] = useState(0);
  const layoutSettings = useLayoutSettings(isLayoutPreview);
  const lightingSettings = defaultPacketLightingSettings;
  const poseSettings = defaultPacketPoseSettings;
  const tapProgressRef = useRef(0);
  const timeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);
  const revealFlowerPackRef = useRef<HTMLImageElement>(null);
  const audio = useSeedPacketAudio(audioSettings);
  const screenRevealDelayMs = animationSettings.clickFour.revealOnsetMs;
  const landingAnimationStyle = useMemo<LandingAnimationStyle>(
    () => ({
      ...getLayoutCssVariables(layoutSettings),
      "--reveal-footer-delay": `${animationSettings.reward.footerDelayMs}ms`,
      "--reveal-hero-delay": `${animationSettings.reward.heroDelayMs}ms`,
      "--reveal-secondary-delay": `${animationSettings.reward.secondaryDelayMs}ms`,
      "--reveal-title-delay": `${animationSettings.reward.titleDelayMs}ms`,
    }),
    [layoutSettings],
  );
  const packEntranceTransition = useMemo<Transition>(
    () => ({
      ...animationSettings.reward.packSpring,
      delay: animationSettings.reward.heroDelayMs / 1000,
      type: "spring",
    }),
    [animationSettings.reward.heroDelayMs, animationSettings.reward.packSpring],
  );

  const clearQueuedTimeouts = useCallback(() => {
    for (const timeout of timeoutsRef.current) {
      clearTimeout(timeout);
    }

    timeoutsRef.current = [];
  }, []);

  useEffect(() => clearQueuedTimeouts, [clearQueuedTimeouts]);

  useEffect(() => {
    tapProgressRef.current = tapProgress;
  }, [tapProgress]);

  useEffect(() => {
    if (phase !== "revealed" || editorScene) {
      return;
    }

    const interval = setInterval(() => {
      setRevealedScene((current) => (current === "packs" ? "garden" : "packs"));
    }, revealedSceneCycleMs);

    return () => {
      clearInterval(interval);
    };
  }, [editorScene, phase]);

  useEffect(() => {
    if (!editorScene) {
      return;
    }

    clearQueuedTimeouts();
    setPhase(editorScene === "packet" ? "packet" : "revealed");
    setRevealedScene(editorScene === "garden" ? "garden" : "packs");
    setTapProgress(0);
    tapProgressRef.current = 0;
  }, [clearQueuedTimeouts, editorScene]);

  const queueTimeout = useCallback((callback: () => void, delay: number) => {
    const timeout = setTimeout(callback, delay);

    timeoutsRef.current.push(timeout);
  }, []);

  const resetPacket = useCallback(() => {
    clearQueuedTimeouts();
    tapProgressRef.current = 0;
    setPhase("packet");
    setRevealedScene("packs");
    setOpenRun(0);
    setBurstStartedAt(0);
    setTapProgress(0);
  }, [clearQueuedTimeouts]);

  const handlePacketClick = useCallback(() => {
    if (phase !== "packet") {
      return;
    }

    audio.playBackgroundMusic();

    const nextTapProgress = Math.min(tapProgressRef.current + 1, tapCountToOpen);
    tapProgressRef.current = nextTapProgress;
    setTapProgress(nextTapProgress);
    setOpenRun((current) => current + 1);

    if (nextTapProgress === 1) {
      audio.playTapOne();
      return;
    }

    if (nextTapProgress === 2) {
      audio.playTapOne();
      return;
    }

    if (nextTapProgress === 3) {
      audio.playTapTwo();
      return;
    }

    if (nextTapProgress === 4) {
      audio.playReveal();
      setBurstStartedAt(performance.now());
      setPhase("revealing");
      queueTimeout(() => {
        setPhase("revealed");
        setRevealedScene("packs");
        setTapProgress(0);
        tapProgressRef.current = 0;
      }, screenRevealDelayMs);
    }
  }, [audio, phase, queueTimeout, screenRevealDelayMs]);

  const clicksRemaining = Math.max(tapCountToOpen - tapProgress, 0);
  const clickLabel = clicksRemaining === 1 ? "1 click" : `${clicksRemaining} clicks`;
  const shouldRenderRevealScene = phase !== "packet";

  const rewardStaggerActive =
    shouldRenderRevealScene && (phase === "revealing" || revealedScene === "packs");

  return (
    <main
      className={`landing-page landing-page--${phase} landing-page--reveal-${revealedScene}`}
      data-mobile-store={mobileStore}
      data-layout-preview={isLayoutPreview ? "true" : undefined}
      style={landingAnimationStyle}
    >
      <div className="paper-frame">
        <button
          aria-label="Back to unopened seed packet"
          className="skygarden-logo-button"
          onClick={resetPacket}
          onMouseEnter={audio.playUiHover}
          type="button"
        >
          <img alt="Sky Garden" className="skygarden-logo" src={landingPageAssets.paper.logo} />
        </button>
        <div aria-label="Download Sky Garden" className="header-store-badges">
          <a
            aria-label="Get it on Google Play"
            className="header-store-badge header-store-badge--play"
            href="https://play.google.com/"
            onMouseEnter={audio.playUiHover}
          >
            <img alt="" src={landingPageAssets.storeBadges.googlePlay} />
          </a>
          <a
            aria-label="Download on the App Store"
            className="header-store-badge header-store-badge--app"
            href="https://apps.apple.com/"
            onMouseEnter={audio.playUiHover}
          >
            <img alt="" src={landingPageAssets.storeBadges.appStore} />
          </a>
        </div>
        <h1 aria-live="polite" className="paper-title paper-title--hero">
          <span className="paper-title__copy paper-title__copy--packet">Click to open</span>
          <span className="paper-title__copy paper-title__copy--reveal paper-title__copy--reveal-packs">
            Focus to earn Mystery Packs
          </span>
          <span className="paper-title__copy paper-title__copy--reveal paper-title__copy--reveal-garden">
            Grow your dream garden
          </span>
        </h1>
        <section
          aria-hidden={phase === "revealed"}
          aria-label="Mystery seed packet"
          className="landing-scene landing-scene--packet"
        >
          <div className="layout-opening-pack-group">
            <span aria-hidden="true" className="packet-halo" />
            <span aria-hidden="true" className="packet-shadow" />
            <span aria-hidden="true" className="packet-star-particles">
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
              <span />
            </span>
            <SeedPacketMysteryPack
              animation={animationSettings}
              burstStartedAt={burstStartedAt}
              lighting={lightingSettings}
              phase={phase}
              pose={poseSettings}
              revealTargetRef={revealFlowerPackRef}
              spinRun={openRun}
              tapProgress={tapProgress}
            />
            <button
              aria-label={`Click to open mystery pack, ${clickLabel} remaining`}
              className="packet-button"
              disabled={phase !== "packet" || poseSettings.enabled}
              onClick={handlePacketClick}
              type="button"
            >
              <span className="sr-only">Click to open</span>
            </button>
          </div>
          <div aria-hidden="true" className={`paper-dots paper-dots--tap-${tapProgress}`}>
            <span className="paper-dot" />
            <span className="paper-dot" />
            <span className="paper-dot" />
            <span className="paper-dot" />
          </div>
        </section>
        {shouldRenderRevealScene ? (
          <section aria-hidden={false} className="landing-scene landing-scene--reveal">
            <div aria-hidden="true" className="reveal-slide reveal-slide--packs">
              <div className="reveal-packs-cluster">
                <motion.img
                  alt=""
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  aria-hidden="true"
                  className="reveal-asset reveal-asset--motion reveal-asset--flower-pack"
                  initial={{ opacity: 0, scale: 0.92, y: "3.05cqw" }}
                  ref={revealFlowerPackRef}
                  src={landingPageAssets.reveal.flowerPack}
                  transition={packEntranceTransition}
                />
                <motion.img
                  alt=""
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  aria-hidden="true"
                  className="reveal-asset reveal-asset--motion reveal-asset--tree-pack"
                  initial={{ opacity: 0, scale: 0.92, y: "3.05cqw" }}
                  src={landingPageAssets.reveal.treePack}
                  transition={packEntranceTransition}
                />
              </div>
              <RewardStaggerRow
                active={rewardStaggerActive}
                entranceDelayMs={animationSettings.reward.rewardImagesDelayMs}
                layouts={animationSettings.rewardImages}
                replayToken={0}
                revealAudioDelayMs={audioSettings.revealDelayMs}
                revealAudioPlaybackRate={audioSettings.revealPlaybackRate}
              />
            </div>
            <div aria-hidden="true" className="reveal-slide reveal-slide--garden">
              <img
                alt=""
                className="reveal-garden-image"
                src={landingPageAssets.reveal.garden}
                style={getGardenImageStyle(animationSettings.gardenImage)}
              />
            </div>
            <h2 className="paper-title paper-title--tagline">Make focusing fun</h2>
            <div aria-label="Download Sky Garden" className="store-badges">
              <a
                aria-label="Get it on Google Play"
                className="store-badge store-badge--play"
                href="https://play.google.com/"
                onMouseEnter={audio.playUiHover}
              >
                <img alt="" src={landingPageAssets.storeBadges.googlePlay} />
              </a>
              <a
                aria-label="Download on the App Store"
                className="store-badge store-badge--app"
                href="https://apps.apple.com/"
                onMouseEnter={audio.playUiHover}
              >
                <img alt="" src={landingPageAssets.storeBadges.appStore} />
              </a>
            </div>
            <footer className="reveal-footer">
              <img
                alt=""
                aria-hidden="true"
                className="reveal-footer__logo"
                src={landingPageAssets.paper.logo}
              />
              <nav aria-label="Footer">
                <a href="/privacy">Privacy Policy</a>
                <a href="/terms">Terms of Use</a>
                <a href="mailto:hi@anywhere.garden?subject=Sky%20Garden%20Support">Contact us</a>
              </nav>
              <small className="reveal-footer__copyright">© 2026 Tinker AI Pty Ltd</small>
              <div className="social-links">
                <span>Follow us</span>
                <a
                  aria-label="TikTok"
                  href="https://www.tiktok.com/"
                  onMouseEnter={audio.playUiHover}
                >
                  <TikTokIcon />
                </a>
                <a
                  aria-label="Instagram"
                  href="https://www.instagram.com/"
                  onMouseEnter={audio.playUiHover}
                >
                  <InstagramIcon />
                </a>
                <a
                  aria-label="Join our Discord and claim a mystery gift!"
                  className="discord-community-link"
                  href="https://discord.gg/2XFXuq3rQ"
                  onMouseEnter={audio.playUiHover}
                  rel="noreferrer"
                  target="_blank"
                >
                  <span aria-hidden="true" className="discord-prompt">
                    <span className="discord-prompt__surface">
                      Join our Discord and claim a mystery gift!
                    </span>
                  </span>
                  <span className="discord-community-icon">
                    <DiscordIcon />
                  </span>
                </a>
              </div>
            </footer>
          </section>
        ) : null}
      </div>
    </main>
  );
}
